import { ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import { uploadDocument } from '../assets/uploadFile.js';
import type { FileUploadUploaderEvent } from 'primevue/fileupload';

export function useSupplementaryUpload() {
  const store = useStore();
  const toast = useToast();
  
  const isUploading = ref(false);

  const handleSupplementaryUpload = async (event: FileUploadUploaderEvent, sessionId?: string) => {
    isUploading.value = true;
    const file = Array.isArray(event.files) ? event.files[0] : event.files;
    const mimeType = file.type;
    const fileName = file.name;
    const uploadType = "mp-supplementary";
    
    toast.add({
      severity: "secondary",
      detail: "Uploading file in progress...",
      group: "notification",
      life: 1000,
    });

    try {
      const uploadedFileInfo = await uploadDocument(fileName, mimeType, file, uploadType, sessionId, store);
      toast.add({
        severity: "secondary",
        summary: "File uploaded successfully",
        detail:
            "Your supplementary file has been uploaded and is now available to the note-taker.",
        life: 3000,
        group: "notification",
      });
      
      // Add uploaded file to store
      await store.dispatch('itemList/addUploadToSession', { sessionId, upload: uploadedFileInfo});
      
      return uploadedFileInfo;
    } catch (err) {
        console.error("File upload failed:", err);
        toast.add({
          severity: "error",
          detail: "File upload failed. Please try again.",
          group: "notification",
          life: 3000,
        });
        throw err;
    } finally {
        isUploading.value = false;
    }
  }

  const triggerFileUpload = (sessionId?: string) => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf';
    fileInput.style.display = 'none';
    
    fileInput.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        // Create a FileUploadUploaderEvent-like object
        const uploadEvent = { files: file } as FileUploadUploaderEvent;
        handleSupplementaryUpload(uploadEvent, sessionId);
      }
      // Clean up
      document.body.removeChild(fileInput);
    };
    
    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  }

  return {
    isUploading,
    handleSupplementaryUpload,
    triggerFileUpload
  };
}
